import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import {
  Component,
  lazy,
  Suspense,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import type {
  ComponentProps as XMarkdownComponentProps,
  XMarkdownProps,
} from '@ant-design/x-markdown';

import { sanitizeSvg } from '../lib/sanitizeSvg.js';
import { toInAppCrmHref } from '../lib/crmInAppHref.js';

const XMarkdownRenderer = lazy(async () => {
  const module = await import('@ant-design/x-markdown');
  return { default: module.default };
});

class MarkdownRenderBoundary extends Component<
  { readonly children: ReactNode; readonly fallbackContent: string },
  { readonly failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { readonly failed: true } {
    return { failed: true };
  }

  componentDidUpdate(prevProps: {
    readonly children: ReactNode;
    readonly fallbackContent: string;
  }): void {
    if (this.state.failed && prevProps.fallbackContent !== this.props.fallbackContent) {
      this.setState({ failed: false });
    }
  }

  render(): ReactNode {
    if (this.state.failed) {
      return (
        <span
          className="crm-chat-markdown__fallback whitespace-pre-wrap"
          data-testid="chat-markdown-render-fallback"
        >
          {this.props.fallbackContent}
        </span>
      );
    }
    return this.props.children;
  }
}

export const CHAT_MARKDOWN_MAX_SOURCE_BYTES = 256 * 1024;
export const CHAT_MARKDOWN_JSON_MAX_SOURCE_BYTES = 256 * 1024;
export const CHAT_MARKDOWN_JSON_MAX_DEPTH = 20;
export const CHAT_MARKDOWN_JSON_MAX_RENDERED_NODES = 5_000;
export const CHAT_MARKDOWN_MERMAID_MAX_SOURCE_BYTES = 64 * 1024;
export const CHAT_MARKDOWN_MERMAID_MAX_GRAPH_ITEMS = 500;
export const CHAT_MARKDOWN_MERMAID_OUTPUT_MAX_BYTES = 512 * 1024;
export const CHAT_MARKDOWN_MERMAID_TIMEOUT_MS = 2_000;

const MAILTO_ADDRESS_PATTERN = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
const MARKDOWN_SAFE_URL_PATTERN =
  /^(?:https:\/\/[^\s<>"']+|mailto:[^\s<>"'?]+@[^\s<>"'?]+\.[^\s<>"'?]+|\/(?!\/)[^\s<>"']*|[#?][^\s<>"']*)$/i;
const MERMAID_FORBIDDEN_PATTERN =
  /(?:^\s*click\s+|^\s*href\s+|javascript:|data:|file:|https?:\/\/|<script|<iframe|<img|<svg)/im;
const MERMAID_EDGE_PATTERN = /(?:-->|---|--|==>|-.->|<-->|<--|<-.->|<-->|~~~)/g;
const MERMAID_IDENTIFIER_PATTERN = /\b[A-Za-z][A-Za-z0-9_-]*\b/g;
const MERMAID_RESERVED_WORDS = new Set([
  'accDescr',
  'accTitle',
  'activate',
  'actor',
  'alt',
  'and',
  'class',
  'classDef',
  'deactivate',
  'direction',
  'else',
  'end',
  'flowchart',
  'graph',
  'loop',
  'note',
  'opt',
  'participant',
  'pie',
  'sequenceDiagram',
  'stateDiagram',
  'subgraph',
  'title',
]);

type BoundStatus =
  | 'ok'
  | 'too-large'
  | 'too-deep'
  | 'too-many-nodes'
  | 'invalid'
  | 'unsafe'
  | 'timeout'
  | 'render-error';

interface BoundResult {
  status: BoundStatus;
  reason?: string;
}

interface JsonPreviewResult extends BoundResult {
  formatted?: string;
}

interface MermaidSvgResult extends BoundResult {
  svg?: string;
}

type MarkdownAnchorProps = XMarkdownComponentProps<{
  href?: string | undefined;
  title?: string | undefined;
}>;

type MarkdownCodeProps = XMarkdownComponentProps<{
  block?: boolean | undefined;
  lang?: string | undefined;
}>;

type ParsedElementNode = {
  name?: string;
  attribs?: Record<string, string | undefined>;
  children?: unknown[];
};

export const CHAT_MARKDOWN_DOMPURIFY_CONFIG: NonNullable<XMarkdownProps['dompurifyConfig']> = {
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  ALLOWED_URI_REGEXP: MARKDOWN_SAFE_URL_PATTERN,
  FORBID_ATTR: [
    'action',
    'formaction',
    'onabort',
    'onerror',
    'onload',
    'onclick',
    'onmouseover',
    'poster',
    'src',
    'srcset',
  ],
  FORBID_TAGS: [
    'audio',
    'base',
    'button',
    'canvas',
    'embed',
    'form',
    'iframe',
    'img',
    'input',
    'link',
    'math',
    'meta',
    'object',
    'picture',
    'script',
    'select',
    'source',
    'style',
    'svg',
    'textarea',
    'video',
  ],
};

function getUtf8ByteLength(value: string): number {
  return new Blob([value]).size;
}

function hasControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 31 || codePoint === 127;
  });
}

function getBoundReason(result: BoundResult): string {
  switch (result.status) {
    case 'too-large':
      return i18n.t(
        'crm.features.features.ai.chat.components.boundedmarkdownrenderer.blok_slishkom_bolshoy_dlya_bezopasnogo_rende',
      );
    case 'too-deep':
      return i18n.t(
        'crm.features.features.ai.chat.components.boundedmarkdownrenderer.json_slishkom_glubokiy_dlya_bezopasnogo_rend',
      );
    case 'too-many-nodes':
      return i18n.t(
        'crm.features.features.ai.chat.components.boundedmarkdownrenderer.json_soderzhit_slishkom_mnogo_uzlov_dlya_bezo',
      );
    case 'unsafe':
      return i18n.t(
        'crm.features.features.ai.chat.components.boundedmarkdownrenderer.blok_soderzhit_nebezopasnye_ssylki_ili_call',
      );
    case 'timeout':
      return i18n.t(
        'crm.features.features.ai.chat.components.boundedmarkdownrenderer.render_mermaid_prevysil_bezopasnyy_limit_v',
      );
    case 'render-error':
      return i18n.t(
        'crm.features.features.ai.chat.components.boundedmarkdownrenderer.mermaid_ne_smog_bezopasno_otrenderit_diag',
      );
    case 'invalid':
      return i18n.t(
        'crm.features.features.ai.chat.components.boundedmarkdownrenderer.blok_ne_raspoznan_kak_bezopasnyy_format',
      );
    case 'ok':
      return '';
  }
}

export function isAllowedMarkdownUrl(input: string | undefined): boolean {
  if (!input) {
    return false;
  }

  const value = input.trim();
  if (!value || hasControlCharacter(value) || value.startsWith('//')) {
    return false;
  }

  if (value.startsWith('/') && !value.startsWith('//')) {
    return true;
  }

  if (value.startsWith('#') || value.startsWith('?')) {
    return !value.includes('://');
  }

  if (/^https:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'https:' && parsed.hostname.length > 0;
    } catch {
      return false;
    }
  }

  if (/^mailto:/i.test(value)) {
    const address = value.slice('mailto:'.length);
    return MAILTO_ADDRESS_PATTERN.test(address);
  }

  return false;
}

export function validateJsonPreviewSource(source: string): JsonPreviewResult {
  if (getUtf8ByteLength(source) > CHAT_MARKDOWN_JSON_MAX_SOURCE_BYTES) {
    return { status: 'too-large' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch {
    return { status: 'invalid' };
  }

  const stack: Array<{ value: unknown; depth: number }> = [{ value: parsed, depth: 1 }];
  let renderedNodes = 0;

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      continue;
    }

    renderedNodes += 1;
    if (renderedNodes > CHAT_MARKDOWN_JSON_MAX_RENDERED_NODES) {
      return { status: 'too-many-nodes' };
    }

    if (current.depth > CHAT_MARKDOWN_JSON_MAX_DEPTH) {
      return { status: 'too-deep' };
    }

    if (Array.isArray(current.value)) {
      current.value.forEach((item) => stack.push({ value: item, depth: current.depth + 1 }));
    } else if (typeof current.value === 'object' && current.value !== null) {
      Object.values(current.value).forEach((item) =>
        stack.push({ value: item, depth: current.depth + 1 }),
      );
    }
  }

  return {
    status: 'ok',
    formatted: JSON.stringify(parsed, null, 2),
  };
}

export function validateMermaidSource(source: string): BoundResult {
  if (getUtf8ByteLength(source) > CHAT_MARKDOWN_MERMAID_MAX_SOURCE_BYTES) {
    return { status: 'too-large' };
  }

  if (MERMAID_FORBIDDEN_PATTERN.test(source)) {
    return { status: 'unsafe' };
  }

  const edgeCount = source.match(MERMAID_EDGE_PATTERN)?.length ?? 0;
  const identifiers = new Set(
    (source.match(MERMAID_IDENTIFIER_PATTERN) ?? []).filter(
      (value) => !MERMAID_RESERVED_WORDS.has(value),
    ),
  );

  if (edgeCount + identifiers.size > CHAT_MARKDOWN_MERMAID_MAX_GRAPH_ITEMS) {
    return { status: 'too-many-nodes' };
  }

  return { status: 'ok' };
}

function getSafeMermaidSvg(svg: string): MermaidSvgResult {
  if (getUtf8ByteLength(svg) > CHAT_MARKDOWN_MERMAID_OUTPUT_MAX_BYTES) {
    return { status: 'too-large' };
  }

  if (
    /<script|<foreignObject|\son[a-z]+\s*=|(?:href|xlink:href)\s*=\s*["'](?:javascript:|data:|file:|https?:\/\/)/i.test(
      svg,
    )
  ) {
    return { status: 'unsafe' };
  }

  return { status: 'ok', svg };
}

async function renderMermaidSvg(source: string, renderId: string): Promise<MermaidSvgResult> {
  const validation = validateMermaidSource(source);
  if (validation.status !== 'ok') {
    return validation;
  }

  let timeoutId: number | undefined;
  try {
    const module = await import('mermaid');
    module.default.initialize({
      deterministicIds: true,
      htmlLabels: false,
      flowchart: { htmlLabels: false },
      securityLevel: 'strict',
      startOnLoad: false,
      theme: 'base',
    });

    const renderResult = (await Promise.race([
      module.default.render(renderId, source),
      new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(
          () => reject(new Error('Mermaid render timed out')),
          CHAT_MARKDOWN_MERMAID_TIMEOUT_MS,
        );
      }),
    ])) as { svg: string };

    return getSafeMermaidSvg(renderResult.svg);
  } catch (error) {
    if (error instanceof Error && error.message.includes('timed out')) {
      return { status: 'timeout' };
    }

    return { status: 'render-error' };
  } finally {
    // Winning the race leaves the timeout pending; drop it so its closure
    // does not outlive the render.
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}

function isParsedElementNode(value: unknown): value is ParsedElementNode {
  return typeof value === 'object' && value !== null;
}

function collectNodeText(value: unknown): string {
  if (!isParsedElementNode(value)) {
    return '';
  }

  if ('data' in value && typeof value.data === 'string') {
    return value.data;
  }

  return (value.children ?? []).map(collectNodeText).join('');
}

function getCodeBlock(domNode: unknown): { language: string; source: string } | null {
  if (!isParsedElementNode(domNode)) {
    return null;
  }

  const codeNode = (domNode.children ?? []).find(
    (child): child is ParsedElementNode => isParsedElementNode(child) && child.name === 'code',
  );

  if (!codeNode) {
    return null;
  }

  const language =
    codeNode.attribs?.['data-lang']?.trim().split(/\s+/)[0]?.toLowerCase() ??
    codeNode.attribs?.class?.match(/(?:^|\s)language-([^\s]+)/)?.[1]?.toLowerCase() ??
    '';

  return {
    language,
    source: collectNodeText(codeNode).replace(/\n$/, ''),
  };
}

function SafeMarkdownAnchor({ children, href, title }: MarkdownAnchorProps): ReactElement {
  if (!isAllowedMarkdownUrl(href)) {
    return <span className="crm-chat-markdown__blocked-link">{children}</span>;
  }

  const safeHref = toInAppCrmHref(href?.trim() ?? '');
  const isExternal = /^https:\/\//i.test(safeHref);

  return (
    <a
      href={safeHref}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      target={isExternal ? '_blank' : undefined}
      title={title}
    >
      {children}
    </a>
  );
}

function MarkdownCode({ block, children, lang }: MarkdownCodeProps): ReactElement {
  if (block) {
    return <code className={lang ? `language-${lang}` : undefined}>{children}</code>;
  }

  return <code>{children}</code>;
}

function JsonCodeBlock({ source }: { source: string }): ReactElement {
  const result = validateJsonPreviewSource(source);

  if (result.status !== 'ok' || !result.formatted) {
    return (
      <pre className="crm-chat-markdown__fallback" data-testid="chat-markdown-json-fallback">
        {getBoundReason(result)}
      </pre>
    );
  }

  return (
    <pre className="crm-chat-markdown__json" data-testid="chat-markdown-json">
      <code>{result.formatted}</code>
    </pre>
  );
}

function MermaidCodeBlock({ source }: { source: string }): ReactElement {
  const reactId = useId().replace(/[^A-Za-z0-9_-]/g, '');
  const renderId = `crm-chat-mermaid-${reactId}`;
  const validation = useMemo(() => validateMermaidSource(source), [source]);
  const [result, setResult] = useState<MermaidSvgResult>({ status: 'ok' });

  useEffect(() => {
    if (validation.status !== 'ok') {
      setResult(validation);
      return;
    }

    let active = true;
    setResult({ status: 'ok' });
    void renderMermaidSvg(source, renderId).then((nextResult) => {
      if (active) {
        setResult(nextResult);
      }
    });

    return () => {
      active = false;
    };
  }, [renderId, source, validation]);

  if (validation.status !== 'ok') {
    return (
      <pre className="crm-chat-markdown__fallback" data-testid="chat-markdown-mermaid-fallback">
        {getBoundReason(validation)}
      </pre>
    );
  }

  if (result.status === 'ok' && result.svg) {
    return (
      <div
        className="crm-chat-markdown__mermaid"
        data-testid="chat-markdown-mermaid"
        dangerouslySetInnerHTML={{ __html: sanitizeSvg(result.svg) }}
      />
    );
  }

  if (result.status !== 'ok') {
    return (
      <pre className="crm-chat-markdown__fallback" data-testid="chat-markdown-mermaid-fallback">
        {getBoundReason(result)}
      </pre>
    );
  }

  return (
    <pre className="crm-chat-markdown__loading" data-testid="chat-markdown-mermaid-loading">
      {i18n.t('crm.ai_chat.markdown.mermaid_loading')}
    </pre>
  );
}

function MarkdownPre({ children, domNode }: XMarkdownComponentProps): ReactElement {
  const codeBlock = getCodeBlock(domNode);

  if (!codeBlock) {
    return <pre>{children}</pre>;
  }

  if (codeBlock.language === 'json') {
    return <JsonCodeBlock source={codeBlock.source} />;
  }

  if (codeBlock.language === 'mermaid') {
    return <MermaidCodeBlock source={codeBlock.source} />;
  }

  return (
    <pre>
      <code className={codeBlock.language ? `language-${codeBlock.language}` : undefined}>
        {codeBlock.source}
      </code>
    </pre>
  );
}

function MarkdownTable({ children }: XMarkdownComponentProps): ReactElement {
  useI18nSubscription();

  return (
    <div
      aria-label={i18n.t('crm.ai_chat.markdown.table_scroll')}
      className="crm-chat-markdown__table-scroll rounded focus-visible:outline-2 focus-visible:outline-amber-600"
      role="region"
      style={{ maxWidth: '100%', overflowX: 'auto', width: '100%' }}
      tabIndex={0}
    >
      <table
        style={{
          display: 'table',
          margin: 0,
          maxWidth: 'none',
          minWidth: '100%',
          width: 'max-content',
        }}
      >
        {children}
      </table>
    </div>
  );
}

const CHAT_MARKDOWN_COMPONENTS: NonNullable<XMarkdownProps['components']> = {
  a: SafeMarkdownAnchor,
  code: MarkdownCode,
  pre: MarkdownPre,
  table: MarkdownTable,
};

export function BoundedMarkdownRenderer({
  content,
  isStreaming,
}: {
  content: string;
  isStreaming: boolean;
}): ReactElement {
  useI18nSubscription();
  if (getUtf8ByteLength(content) > CHAT_MARKDOWN_MAX_SOURCE_BYTES) {
    return (
      <span className="crm-chat-markdown__fallback" data-testid="chat-markdown-source-fallback">
        {i18n.t(
          'crm.features.features.ai.chat.components.boundedmarkdownrenderer.soobshchenie_slishkom_bolshoe_dlya_bezopasnogo_',
        )}
      </span>
    );
  }

  return (
    <MarkdownRenderBoundary fallbackContent={content}>
      <Suspense fallback={<span className="whitespace-pre-wrap">{content}</span>}>
        <XMarkdownRenderer
          components={CHAT_MARKDOWN_COMPONENTS}
          content={content}
          dompurifyConfig={CHAT_MARKDOWN_DOMPURIFY_CONFIG}
          escapeRawHtml
          rootClassName="crm-chat-markdown"
          streaming={{ hasNextChunk: isStreaming, tail: isStreaming }}
        />
      </Suspense>
    </MarkdownRenderBoundary>
  );
}
